import { trackVintageOrOffersPageView } from '@helpers/analytics';
import {
  GTM_ECOMMERCE_FIELD_NAMES,
  GTM_EVENT_NAMES,
  GTM_FIELD_NAMES,
  pushToDataLayer,
} from '@helpers/googleTagManager';
import { getUrlSearchParams } from '@helpers/urlSearchParams';
import { PriceType } from '@webtypes/common';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { fetchPriceSubtotal } from 'vivino-js/api/prices';
import { parseIntFromString } from 'vivino-js/helpers/number';

import { isLinkoutMerchant, isVCMerchant } from '@lib/merchants';

import PurchaseAvailabilityPPC from './components/PurchaseAvailabilityPPC';

import PurchaseAvailabilityPresentational from './PurchaseAvailability.presentational';
import styles from './purchaseAvailability.scss';

const PurchaseAvailability = ({
  className,
  availability,
  currency,
  vintage,
  merchant,
  price,
  countryCode,
  stateCode,
  valueForMoney,
  onSelectChange,
  marketInformation,
  isVintageSubstitutionDisabled,
  merchantsWithPrices,
  shouldTrack = true,
  dropdownCheckoutPrices,
}) => {
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasTracked, setHasTracked] = useState(false);
  const [loadingQuantity, setLoadingQuantity] = useState(true);
  const [bottleCountState, setBottleCountState] = useState(null);
  const [quantityHasChanged, setQuantityHasChanged] = useState(false);

  const handleQuantityChange = (bottleCount) => {
    setBottleCountState(bottleCount);
    setQuantityHasChanged(true);
  };

  const handleQuantityLoading = () => {
    setLoadingQuantity(true);
  };

  const onLoaded = () => {
    const purchaseAvailabilityNode = document.getElementsByClassName('purchaseAvailability')?.[0];
    if (purchaseAvailabilityNode) {
      purchaseAvailabilityNode.classList?.remove('loading');
    }
    const belowTheFoldNodes = document.getElementsByClassName('belowTheFold');
    Array.from(belowTheFoldNodes).forEach((belowTheFoldNode) => {
      belowTheFoldNode.classList?.remove('loading');
    });
  };

  const calculatePrice = ({ bottleCount }) => {
    const priceId = price?.id;
    if (!priceId || (!isVCMerchant(merchant) && !isLinkoutMerchant(merchant))) {
      setLoadingQuantity(false);
      return;
    }

    fetchPriceSubtotal({ priceId, bottleCount })
      .then((result) => {
        setCalculatedPrice(result);
        setErrorMessage(null);
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setLoadingQuantity(false);
        onLoaded();
      });
  };

  // These should only be called more than once if instantiated by the PurchaseAvailabilityList
  useEffect(() => {
    if ((isVCMerchant(merchant) || isLinkoutMerchant(merchant)) && price) {
      // Need calculate price every time quantity changes
      // calculate price may return different price than the requested price_id
      // depending on bottle_count
      calculatePrice({ bottleCount: bottleCountState });
    }
  }, [price, bottleCountState]);

  useEffect(() => {
    const searchParams = getUrlSearchParams();
    const forcedBottleCount = parseIntFromString(searchParams.get('bottle_count'), null);
    if (forcedBottleCount) {
      setBottleCountState(forcedBottleCount);
    }
  }, []);

  useEffect(() => {
    if (shouldTrack && !hasTracked) {
      setHasTracked(true);

      const priceType = availability?.price?.type || price?.type;
      const priceId = availability?.price?.id || price?.id;
      const vintageId = availability?.vintage?.id || vintage?.id;
      const priceAmount = availability?.price?.amount || price?.amount;

      trackVintageOrOffersPageView({
        priceId,
        priceType,
        vintageId,
        merchantId: merchant?.id,
        wineId: vintage?.wine?.id,
        priceAmount: priceAmount,
        currencyCode: price?.currency?.code,
        wineryId: vintage?.wine?.winery?.id,
        sponsored: vintage?.wine?.winery?.sponsored_entity_type,
        rating: vintage?.statistics?.ratings_average,
        buyButton: !!price,
      });

      if (price && (price.type === PriceType.VC || price.type === PriceType.XDO)) {
        // Clear ecommerce data
        pushToDataLayer({ [GTM_FIELD_NAMES.ECOMMERCE]: null });

        pushToDataLayer({
          price: price.amount,
          [GTM_FIELD_NAMES.PRICE_ID]: Number(priceId),
          [GTM_FIELD_NAMES.VINTAGE_ID]: Number(vintageId),
          [GTM_FIELD_NAMES.CURRENCY_CODE]: price?.currency?.code,
          [GTM_FIELD_NAMES.EVENT]: GTM_EVENT_NAMES.VIEW_BUYABLE_WINE_PAGE,
          [GTM_FIELD_NAMES.ECOMMERCE]: {
            [GTM_ECOMMERCE_FIELD_NAMES.VALUE]: priceAmount,
            [GTM_ECOMMERCE_FIELD_NAMES.CURRENCY]: price?.currency?.code,
            [GTM_ECOMMERCE_FIELD_NAMES.ITEMS]: [
              {
                item_name: vintage?.name,
                item_brand: vintage?.wine?.winery?.name,
                item_id: priceId,
                item_category: 'wine',
                price: priceAmount,
                quantity: 1,
              },
            ],
          },
        });
      }
    }
  }, []);

  if (!merchant || (!isVCMerchant(merchant) && !isLinkoutMerchant(merchant)) || !price) {
    return (
      <PurchaseAvailabilityPPC
        vintage={vintage}
        availability={availability}
        marketInformation={marketInformation}
        onLoaded={onLoaded}
      />
    );
  }

  return (
    <div className={cx(styles.purchaseAvailability, className)}>
      <PurchaseAvailabilityPresentational
        availability={availability}
        calculatedPrice={calculatedPrice}
        currency={currency}
        countryCode={countryCode}
        errorMessage={errorMessage}
        loadingQuantity={loadingQuantity}
        merchant={merchant}
        merchantsWithPrices={merchantsWithPrices}
        onQuantityChange={handleQuantityChange}
        onQuantityChanging={handleQuantityLoading}
        quantityHasChanged={quantityHasChanged}
        onSelectChange={onSelectChange}
        price={price}
        stateCode={stateCode}
        vintage={vintage}
        valueForMoney={valueForMoney}
        dropdownCheckoutPrices={dropdownCheckoutPrices}
        isVintageSubstitutionDisabled={isVintageSubstitutionDisabled}
      />
    </div>
  );
};

PurchaseAvailability.propTypes = {
  availability: PropTypes.object,
  className: PropTypes.string,
  countryCode: PropTypes.string,
  currency: PropTypes.object,
  errorMessage: PropTypes.string,
  merchant: PropTypes.object,
  price: PropTypes.object,
  stateCode: PropTypes.string,
  trackingScreen: PropTypes.string,
  vintage: PropTypes.object,
  //Optional list props
  dropdownCheckoutPrices: PropTypes.array,
  onSelectChange: PropTypes.func,
  isVintagePage: PropTypes.bool,
  marketInformation: PropTypes.object,
  isVintageSubstitutionDisabled: PropTypes.bool,
  shouldTrack: PropTypes.bool,
  merchantsWithPrices: PropTypes.array,
  valueForMoney: PropTypes.object,
};

export default PurchaseAvailability;
